import { useState } from 'react'
import { Link } from 'wouter'
import { useAdminFetch } from '../hooks/useAdminAPI'
import {
  PageHeader, AdminButton, StatusBadge,
  MetricStrip, Metric, Section, DataTable, ViewState,
} from '../components'
import { formatDate } from '../utils/format'
import styles from './Clusters.module.css'

export function Clusters({ params }) {
  if (params?.id) return <ClusterDetail id={params.id} />
  return <ClusterList />
}

function ClusterList() {
  const { data, loading, error, refresh } = useAdminFetch('/admin/clusters')
  const [search, setSearch] = useState('')

  if (loading && !data) return <ViewState variant="loading" />
  if (error && !data) return <ViewState variant="error" message={error} onRetry={refresh} />

  const clusters = data?.clusters ?? []
  const filtered = search
    ? clusters.filter(c =>
        c.canonicalName.toLowerCase().includes(search.toLowerCase())
      )
    : clusters

  return (
    <div className={styles.page}>
      <PageHeader
        title="Clusters"
        subtitle={`${clusters.length} product clusters`}
        actions={
          <div className={styles.headerActions}>
            <div className={styles.searchWrap}>
              <input
                className={styles.search}
                type="text"
                placeholder="Search clusters…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <AdminButton
              variant="icon-only"
              icon="refresh"
              label="Refresh data"
              onClick={refresh}
              disabled={loading}
            />
          </div>
        }
      />

      {filtered.length === 0 && data ? (
        <ViewState variant="empty" message={search ? `No clusters matching "${search}"` : 'No clusters found'} />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th className={styles.idCol}>ID</th>
              <th>Name</th>
              <th>Category</th>
              <th className={styles.numberCol}>Products</th>
              <th className={styles.numberCol}>Aliases</th>
              <th>Illustration</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(cluster => (
              <tr key={cluster.id}>
                <td className={styles.mono}>{cluster.id}</td>
                <td>
                  <Link href={`/clusters/${cluster.id}`} className={styles.clusterLink}>
                    {cluster.canonicalName}
                  </Link>
                </td>
                <td>
                  <CategoryBadge category={cluster.category} />
                </td>
                <td className={styles.number}>{cluster.productCount}</td>
                <td className={styles.number}>{cluster.aliasCount}</td>
                <td>
                  {cluster.hasIllustration
                    ? <StatusBadge status="ok" label="Yes" />
                    : <span className={styles.tertiary}>—</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  )
}

function ClusterDetail({ id }) {
  const { data, loading, error, refresh } = useAdminFetch(`/admin/clusters?id=${id}`)

  if (loading && !data) return <ViewState variant="loading" message={`Loading cluster ${id}…`} />
  if (error && !data) return <ViewState variant="error" message={error} onRetry={refresh} />
  if (!data?.cluster) return <ViewState variant="empty" message="Cluster not found" />

  const { cluster, products, aliases } = data

  return (
    <div className={styles.page}>
      <PageHeader
        title={cluster.canonicalName}
        subtitle={`Cluster #${cluster.id}`}
        breadcrumbs={[{ label: 'Clusters', href: '/clusters' }]}
        actions={
          <AdminButton
            variant="icon-only"
            icon="refresh"
            label="Refresh data"
            onClick={refresh}
            disabled={loading}
          />
        }
      />

      <MetricStrip>
        <Metric label="Products" value={products.length} />
        <Metric label="Aliases" value={aliases.length} />
        <Metric label="Category" value={cluster.category} />
      </MetricStrip>

      <Section title="Products">
        <DataTable>
          <thead>
            <tr>
              <th>Supermarket</th>
              <th>Product name</th>
              <th>Unit</th>
              <th>Unit (parsed)</th>
              <th>First seen</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.id}>
                <td><SupermarketDot supermarket={p.supermarket} /></td>
                <td>{p.productName}</td>
                <td className={styles.mono}>{p.unit}</td>
                <td className={styles.mono}>
                  {p.unitMeasurement
                    ? `${p.unitQuantity ?? '?'} ${p.unitLabel ?? '?'} (${p.unitMeasurement})`
                    : <span className={styles.tertiary}>—</span>
                  }
                </td>
                <td className={styles.mono}>{formatDate(p.firstSeen)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </Section>

      <Section title="Aliases">
        <DataTable>
          <thead>
            <tr>
              <th>Normalized name</th>
              <th>Display name</th>
              <th>Source</th>
              <th>Method</th>
              <th className={styles.numberCol}>Score</th>
            </tr>
          </thead>
          <tbody>
            {aliases.map(a => (
              <tr key={a.id}>
                <td className={styles.mono}>{a.normalizedName}</td>
                <td>{a.displayName}</td>
                <td><SourceBadge source={a.source} /></td>
                <td className={styles.tertiary}>{a.resolutionMethod ?? '—'}</td>
                <td className={styles.number}>
                  {a.resolutionScore != null
                    ? (a.resolutionScore * 100).toFixed(1) + '%'
                    : '—'
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </Section>
    </div>
  )
}

function SupermarketDot({ supermarket }) {
  return (
    <span className={styles.supermarketDot}>
      <span className={styles.dot} data-supermarket={supermarket} />
      {supermarket}
    </span>
  )
}

function CategoryBadge({ category }) {
  return (
    <span className={`${styles.categoryBadge} ${styles[`cat_${category}`] || ''}`}>
      {category}
    </span>
  )
}

function SourceBadge({ source }) {
  const variant =
    source === 'vectorize' ? styles.badgeSemantic
    : source === 'exact' ? styles.badgeExact
    : source === 'manual' ? styles.badgeManual
    : styles.badgeNeutral

  return <span className={`${styles.badge} ${variant}`}>{source}</span>
}
